import React, { useEffect, useState } from 'react';
import { Loader2, Check } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { fetchShareCard, saveShareCard, setHideFromSearch, type ShareCard, type Site } from '@/services/siteApi';

// Settings: hide the heyjoesites.com address from search engines, and the card a
// shared link shows in Messages, WhatsApp or Slack (siteShare.js on the server).
const INK = 'var(--hj-ink)';
const MUTED = 'var(--hj-ink-2)';
const panel: React.CSSProperties = { background: '#fff', border: '1px solid var(--hj-hairline-soft)', borderRadius: 16, padding: 24 };
const h2: React.CSSProperties = { margin: 0, fontFamily: 'var(--hj-font-display)', fontWeight: 800, fontSize: 18, letterSpacing: '-0.01em', color: INK };
const small: React.CSSProperties = { fontSize: 14, color: MUTED, margin: 0, lineHeight: 1.45 };
const errText: React.CSSProperties = { ...small, color: 'var(--hj-pink-ink)' };
const label: React.CSSProperties = { fontWeight: 600, fontSize: 14, color: INK };
const input: React.CSSProperties = { width: '100%', boxSizing: 'border-box', minHeight: 44, border: '1px solid var(--hj-hairline)', borderRadius: 10, padding: '10px 12px', font: '15px var(--hj-font-ui)', color: INK, background: '#fff' };
const quiet: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 40, padding: '8px 14px', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, background: INK, color: '#fff', border: 0, borderRadius: 10, cursor: 'pointer' };
const linkBtn: React.CSSProperties = { background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: INK, textDecoration: 'underline', cursor: 'pointer', minHeight: 32 };

export const SiteSearchPanel: React.FC<{ site: Site; onSite: (s: Site) => void }> = ({ site, onSite }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const address = site.url.replace('https://', '');
  return (
    <section className="flex flex-col" style={{ ...panel, gap: 10 }}>
      <h2 style={h2}>Search engines</h2>
      <label className="flex items-start" style={{ gap: 10, cursor: busy ? 'wait' : 'pointer' }}>
        <Checkbox checked={!!site.hideFromSearch} disabled={busy} style={{ marginTop: 2 }} onCheckedChange={async (v) => {
          setBusy(true); setErr(null);
          try { const r = await setHideFromSearch(site.id, v === true); onSite({ ...site, hideFromSearch: r.site.hideFromSearch }); } catch (e) { setErr((e as Error).message); }
          setBusy(false);
        }} />
        <span style={{ fontSize: 15, color: INK, lineHeight: 1.45, wordBreak: 'break-word' }}>Hide {address} from Google and other search engines</span>
        {busy && <Loader2 className="w-4 h-4 animate-spin" style={{ flexShrink: 0, marginTop: 3 }} />}
      </label>
      <p style={small}>Anyone with the link still sees the site. Use this while a site waits on its own domain. Your own domain always shows up in search once it’s connected, and preview links are always hidden.</p>
      {err && <p style={errText}>{err}</p>}
    </section>
  );
};

export const SiteSharePanel: React.FC<{ site: Site }> = ({ site }) => {
  const [host, setHost] = useState('');
  const [card, setCard] = useState<ShareCard | null>(null);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [image, setImage] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const load = () => fetchShareCard(site.id).then((r) => {
    setHost(r.host); setCard(r.card);
    setTitle(r.card.custom.title); setDesc(r.card.custom.description); setImage(r.card.custom.image);
  }).catch((e) => setErr((e as Error).message));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [site.id, site.previewVersionId, site.liveVersionId]);
  if (!card) return err ? <section style={panel}><p style={errText}>{err}</p></section> : null;

  const shownImage = image ? (card.images.find((i) => i.src === image)?.url || null) : card.image;
  const shown = { title: title || card.title, description: desc || card.description, image: shownImage };
  const dirty = title !== card.custom.title || desc !== card.custom.description || image !== card.custom.image;
  const save = async (next: { title: string; description: string; image: string }) => {
    setBusy(true); setErr(null); setSaved(false);
    try { await saveShareCard(site.id, next); await load(); setSaved(true); } catch (e) { setErr((e as Error).message); }
    setBusy(false);
  };
  return (
    <section className="flex flex-col" style={{ ...panel, gap: 12 }}>
      <h2 style={h2}>When someone shares your link</h2>
      <p style={small}>This is the card people see when your link is sent in Messages, WhatsApp, Slack or posted online. We fill it in from your home page; change any of it here.</p>

      <div style={{ border: '1px solid var(--hj-hairline)', borderRadius: 14, overflow: 'hidden', maxWidth: 420, background: '#f4f4f1' }}>
        {shown.image
          ? <img src={shown.image} alt="" style={{ display: 'block', width: '100%', aspectRatio: '1.91 / 1', objectFit: 'cover', background: '#ddd' }} />
          : <div className="flex items-center justify-center" style={{ aspectRatio: '1.91 / 1', background: '#e6e6e1', color: INK, fontSize: 14, fontWeight: 600 }}>No photo yet</div>}
        <div style={{ padding: '10px 12px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: INK, lineHeight: 1.3 }}>{shown.title}</div>
          {shown.description && <div style={{ fontSize: 13.5, color: INK, marginTop: 3, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{shown.description}</div>}
          <div style={{ fontSize: 13, color: MUTED, marginTop: 4, wordBreak: 'break-all' }}>{host}</div>
        </div>
      </div>

      {card.images.length > 1 && (
        <div className="flex flex-col" style={{ gap: 6 }}>
          <span style={label}>Photo</span>
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            {card.images.map((i) => {
              const on = (image || card.images.find((x) => x.url === card.image)?.src) === i.src;
              return (
                <button key={i.src} type="button" onClick={() => setImage(i.src)} aria-label="Use this photo" aria-pressed={on}
                  style={{ position: 'relative', width: 84, height: 56, padding: 0, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', border: on ? '3px solid var(--hj-ink)' : '1px solid var(--hj-hairline)', background: '#eee' }}>
                  <img src={i.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  {on && <Check className="w-4 h-4" style={{ position: 'absolute', right: 3, top: 3, color: '#fff', background: INK, borderRadius: 999, padding: 1 }} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <label className="flex flex-col" style={{ gap: 6 }}>
        <span style={label}>Title</span>
        <input value={title} maxLength={90} placeholder={card.title} onChange={(e) => setTitle(e.target.value)} style={input} />
      </label>
      <label className="flex flex-col" style={{ gap: 6 }}>
        <span style={label}>Description</span>
        <textarea value={desc} maxLength={200} rows={3} placeholder={card.description} onChange={(e) => setDesc(e.target.value)} style={{ ...input, resize: 'vertical' }} />
      </label>
      {err && <p style={errText}>{err}</p>}
      {saved && !dirty && <p style={{ ...small, color: '#2f5d1a' }}>Saved. Apps that already showed your link can take a while to pick up the new card.</p>}
      <div className="flex flex-wrap items-center" style={{ gap: 16 }}>
        <button type="button" disabled={busy || !dirty} onClick={() => save({ title: title.trim(), description: desc.trim(), image })} style={{ ...quiet, opacity: busy || !dirty ? 0.45 : 1, cursor: busy || !dirty ? 'not-allowed' : 'pointer' }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
        </button>
        {(card.custom.title || card.custom.description || card.custom.image) && (
          <button type="button" disabled={busy} onClick={() => save({ title: '', description: '', image: '' })} style={linkBtn}>Go back to what we fill in</button>
        )}
      </div>
    </section>
  );
};
